import { create } from 'zustand';
import { teamService } from '../features/team/services/teamService';

import {
    Team,
    TeamMember,
    TeamMemberWithUser,
    User,
} from '../common/types/databaseTypes';
import { useWorkspaceStore } from '@/features/workspace/hooks';
import { workspaceService } from '@/features/workspace/services/workspaceService';

// Team 타입 검증 함수
const isTeam = (value: unknown): value is Team => {
    return (
        typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        'name' in value &&
        typeof (value as Team).id === 'string' &&
        typeof (value as Team).name === 'string'
    );
};

interface TeamState {
    teams: Team[];
    teamMembers: TeamMemberWithUser[];
    currentTeam: Team | null;
    loading: boolean;
    error: string | null;
    fetchTeams: () => Promise<void>;

    setTeams: (teams: Team[]) => void;
    clearTeams: () => void;
    //팀 생성
    setCurrentTeam: (team: Team | null) => Promise<void>;
    //팀 수정
    updateTeamInList: (updatedTeam: Team) => void;
    deleteTeam: (id: string) => void;
    deleteMember: (id: string) => void;
    setTeamMembers: (teamMembers: TeamMemberWithUser[]) => void;
    updateTeamMember: (updatedTeamMembers: TeamMemberWithUser[]) => void;
}

export const useTeamStore = create<TeamState>(set => ({
    teams: [],
    teamMembers: [],
    currentTeam: (() => {
        try {
            const savedTeam = localStorage.getItem('currentTeam');
            if (!savedTeam) return null;

            const parsedTeam = JSON.parse(savedTeam) as Team;
            return isTeam(parsedTeam) ? parsedTeam : null;
        } catch {
            return null;
        }
    })(),
    loading: false,
    error: null,
    fetchTeams: async () => {
        set({ loading: true, error: null });
        try {
            const teams = await teamService.getMyTeams();
            set({ teams, loading: false, error: null });
        } catch (error) {
            set({
                loading: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch teams',
            });
        }
    },

    setTeams: teams => set({ teams, loading: false, error: null }),
    clearTeams: () => set({ teams: [], loading: false, error: null }),
    setCurrentTeam: async currentTeam => {
        if (currentTeam) {
            localStorage.setItem('currentTeam', JSON.stringify(currentTeam));
        } else {
            localStorage.removeItem('currentTeam');
        }
        set({ currentTeam });

        // 팀 변경 시 워크스페이스도 동기화
        if (currentTeam) {
            const { workspace_id } = currentTeam;
            const { setCurrentWorkspace } = useWorkspaceStore.getState();

            const workspace =
                await workspaceService.getWorkspaceById(workspace_id);
            if (workspace) {
                setCurrentWorkspace(workspace);
            }
        }
    },
    updateTeamInList: updatedTeam =>
        set(state => ({
            teams: state.teams.map(team =>
                team.id === updatedTeam.id ? updatedTeam : team
            ),
        })),

    deleteTeam: (id: string) => {
        localStorage.removeItem('currentTeam');
        set(state => ({
            teams: state.teams.filter(team => team.id !== id),
            currentTeam: null,
        }));
    },

    //멤버관리
    setTeamMembers: (teamMembers: TeamMemberWithUser[]) => set({ teamMembers }),
    setAllMember: (id: string) => {
        set({ loading: true, error: null });
    },
    clearTeamMembers: () => set({ teamMembers: [] }),

    inserMember: (member: User, teamMembers: TeamMember) => {
        const teamMember: TeamMemberWithUser = {
            ...teamMembers,
            user: member,
        };
        set(state => ({
            teamMembers: [...state.teamMembers, teamMember],
        }));
    },

    deleteMember: (id: string) => {
        set(state => ({
            teamMembers: state.teamMembers.filter(member => member.id !== id),
        }));
    },

    updateTeamMember: (updatedTeamMembers: TeamMemberWithUser[]) => {
        set({ teamMembers: updatedTeamMembers });
    },
}));
